import React, { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { ArrowLeft, Trash2, Save, Download } from "lucide-react";
import { toast } from "sonner";
import './PDFExportStyles.css';

// Convert File/Blob to base64 for PDF export compatibility
const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    if (!file) {
      resolve(null);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};


// Simple HTML5 Drag & Drop Implementation
const DraggableSection = ({ id, children, onDragStart, onDragOver, onDrop, advancedEdit, className = "" }) => {
  return (
    <div className={`${className} ${advancedEdit ? 'border-2 border-dashed border-blue-300 relative bg-white bg-opacity-95 rounded-lg p-2 shadow-sm' : ''}`}>
      {advancedEdit && (
        <div className="absolute -top-2 -left-2 bg-blue-600 text-white text-xs px-2 py-1 rounded-full shadow-lg font-bold">
          {id.toUpperCase()}
        </div>
      )}
      {children}
    </div>
  );
};

// New component for individual draggable/resizable elements with free positioning
const DraggableElement = ({ 
  id, 
  children, 
  advancedEdit, 
  className = "", 
  resizable = false,
  onResize,
  style = {}
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [size, setSize] = useState({ width: 'auto', height: 'auto' });
  const elementRef = useRef(null);
  const dragStartPos = useRef({ x: 0, y: 0 });
  const initialPos = useRef({ x: 0, y: 0 });
  
  const handleMouseDown = (e) => {
    if (!advancedEdit || isResizing) return;
    
    const rect = elementRef.current.getBoundingClientRect();
    dragStartPos.current = { x: e.clientX, y: e.clientY };
    initialPos.current = { x: position.x, y: position.y };
    setIsDragging(true);
    
    const handleMouseMove = (e) => {
      const deltaX = e.clientX - dragStartPos.current.x;
      const deltaY = e.clientY - dragStartPos.current.y;
      
      setPosition({
        x: initialPos.current.x + deltaX,
        y: initialPos.current.y + deltaY
      });
    };
    
    const handleMouseUp = () => {
      setIsDragging(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    e.preventDefault();
  };
  
  const handleResizeMouseDown = (e) => {
    if (!advancedEdit || !resizable) return;
    
    setIsResizing(true);
    const startX = e.clientX;
    const startY = e.clientY;
    const startWidth = elementRef.current.offsetWidth;
    const startHeight = elementRef.current.offsetHeight;
    
    const handleResizeMove = (e) => {
      const deltaX = e.clientX - startX;
      const deltaY = e.clientY - startY;
      
      setSize({
        width: Math.max(50, startWidth + deltaX) + 'px',
        height: Math.max(30, startHeight + deltaY) + 'px'
      });
    };
    
    const handleResizeUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeUp);
    };
    
    document.addEventListener('mousemove', handleResizeMove);
    document.addEventListener('mouseup', handleResizeUp);
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <div
      ref={elementRef}
      className={`${className} ${advancedEdit ? 'border border-blue-200 relative group' : ''} ${isDragging ? 'opacity-70 z-50' : ''} ${isResizing ? 'z-50' : ''}`}
      style={{
        position: advancedEdit ? 'absolute' : 'relative',
        transform: advancedEdit ? `translate(${position.x}px, ${position.y}px)` : 'none',
        transition: isDragging || isResizing ? 'none' : 'all 0.2s ease',
        cursor: isDragging ? 'grabbing' : (advancedEdit ? 'grab' : 'default'),
        zIndex: isDragging || isResizing ? 1000 : 'auto',
        ...size,
        ...style
      }}
      onMouseDown={handleMouseDown}
    >
      {advancedEdit && (
        <>
          <div className="absolute -top-2 -left-2 bg-blue-500 text-white text-xs px-1 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity cursor-grab select-none pointer-events-none">
            ⋮⋮ DRAG
          </div>
          {resizable && (
            <div 
              className="absolute -bottom-2 -right-2 bg-green-500 text-white text-xs px-1 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity cursor-se-resize select-none"
              onMouseDown={handleResizeMouseDown}
            >
              ↘ RESIZE
            </div>
          )}
        </>
      )}
      {children}
    </div>
  );
};
const SocialLogos = {
  Instagram: () => (
    <svg viewBox="0 0 24 24" className="w-6 h-6">
      <defs>
        <radialGradient id="instagram-gradient" cx="0.5" cy="1" r="1">
          <stop offset="0%" stopColor="#feda75"/>
          <stop offset="25%" stopColor="#fa7e1e"/>
          <stop offset="50%" stopColor="#d62976"/>
          <stop offset="75%" stopColor="#962fbf"/>
          <stop offset="100%" stopColor="#4f5bd5"/>
        </radialGradient>
      </defs>
      <path fill="url(#instagram-gradient)" d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
    </svg>
  ),
  Facebook: () => (
    <svg viewBox="0 0 24 24" className="w-6 h-6" fill="#1877F2">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
    </svg>
  ),
  Twitter: () => (
    <svg viewBox="0 0 24 24" className="w-6 h-6" fill="#1DA1F2">
      <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
    </svg>
  ),
  YouTube: () => (
    <svg viewBox="0 0 24 24" className="w-6 h-6" fill="#FF0000">
      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
    </svg>
  ),
  Spotify: () => (
    <svg viewBox="0 0 24 24" className="w-6 h-6" fill="#1DB954">
      <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.42 1.56-.299.421-1.02.599-1.559.3z"/>
    </svg>
  ),
  TikTok: () => (
    <svg viewBox="0 0 24 24" className="w-6 h-6">
      <path fill="#ff0050" d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
    </svg>
  )
};

const WebPPTGenerator = ({ formData, images, onBack, onSave, kurPrix = [], logosList = [], labelLogos = [], genresList = [] }) => {
  const [editableData, setEditableData] = useState(formData);
  const [isEditing, setIsEditing] = useState(false);
  const [advancedEdit, setAdvancedEdit] = useState(false);
  const [headerEdit, setHeaderEdit] = useState(false); // New state for header editing
  const [printOrientation, setPrintOrientation] = useState('portrait');
  const [autoLayoutMode, setAutoLayoutMode] = useState('standard'); // 'standard', 'compact', 'print-optimized'
  const [templateMode, setTemplateMode] = useState('landscape'); // Default to landscape only
  const [layouts, setLayouts] = useState(['formats', 'media', 'tracklist']);
  const [mediaFilenames, setMediaFilenames] = useState({});

  // Fonction pour formater le prix pour affichage
  const formatPriceDisplay = (prixValue) => {
    if (!prixValue) return '';
    // If already in "CODE-PRICE" format (from dropdown), return as-is
    if (prixValue.includes('-')) return prixValue;
    // If just a code, look up the price
    if (kurPrix && kurPrix.length > 0) {
      const priceItem = kurPrix.find(item => item.code === prixValue);
      if (priceItem) return `${prixValue}-${priceItem.price}`;
    }
    return prixValue;
  };

  // État pour les positions des éléments du header
  const [headerPositions, setHeaderPositions] = useState({
    header_29music: { x: 0, y: 0 },
    header_kuroneko: { x: 0, y: 0 },
    header_coup_coeur: { x: 0, y: 0 },
    header_genre: { x: 0, y: 0 },
    header_contact: { x: 0, y: 0 }
  });

  const presentationRef = useRef();
  const artboardRef = useRef();
  
  // Simple state for element positions (no complex context)
  // 🎯 LAYOUT AUTOMATIQUE INTELLIGENT
  // Calcule dynamiquement les tailles de colonnes selon le contenu
  const calculateLayoutColumns = () => {
    const trackCount = editableData.tracklist.length;
    const concertCount = editableData.concerts.length;
    const formatCount = (editableData.cd ? 1 : 0) + (editableData.lp ? 1 : 0) + (editableData.cd_lp ? 1 : 0);
    
    // Règles d'adaptation :
    // - Beaucoup de titres (>15) : tracklist plus large
    // - Beaucoup de concerts (>5) : concerts plus large
    // - Peu de titres (<8) : moins d'espace tracklist
    
    let leftColSpan = 3;  // PC Express + Formats (col-span-3 par défaut)
    let centerColSpan = 5; // Tracklist (col-span-5 par défaut)
    let rightColSpan = 4;  // Argumentaire + Concerts (col-span-4 par défaut)
    
    // Adaptation selon le nombre de titres
    if (trackCount > 15) {
      centerColSpan = 6;  // Plus d'espace pour la tracklist
      rightColSpan = 3;   // Moins d'espace à droite
    } else if (trackCount < 8) {
      centerColSpan = 4;  // Moins d'espace pour la tracklist
      rightColSpan = 5;   // Plus d'espace à droite
    }
    
    // Adaptation si beaucoup de concerts
    if (concertCount > 5) {
      rightColSpan += 1;  // Plus d'espace pour les concerts
      centerColSpan -= 1; // On prend sur la tracklist
    }
    
    return { leftColSpan, centerColSpan, rightColSpan };
  };
  
  // Calcule la taille de police adaptative pour la tracklist
  const getAdaptiveTracklistFontSize = () => {
    if (!editableData?.tracklist) return 'text-xs';
    const trackCount = editableData.tracklist.length;
    
    if (trackCount > 20) return 'text-[8px]';  // Très compact
    if (trackCount > 15) return 'text-[9px]';  // Compact
    if (trackCount > 10) return 'text-[10px]'; // Normal petit
    return 'text-xs'; // Normal (12px)
  };
  
  // Calcule la taille de police adaptative pour les concerts
  const getAdaptiveConcertFontSize = () => {
    if (!editableData?.concerts) return 'text-xs';
    const concertCount = editableData.concerts.length;
    
    if (concertCount > 7) return 'text-[9px]';  // Très compact
    if (concertCount > 5) return 'text-[10px]'; // Compact
    return 'text-xs'; // Normal
  };

  // Font size utility functions
  const getCustomFontSize = (element, defaultSize = 'text-sm') => {
    const fontSizeMapping = {
      'tracklist': editableData.font_size_tracklist,
      'argumentaire': editableData.font_size_argumentaire,
      'catalog': editableData.font_size_catalog,
      'artist_title': editableData.font_size_artist_title,
      'title': editableData.font_size_title,
      'promotion': editableData.font_size_promotion,
      'concert': editableData.font_size_concert,
      'cdlp': editableData.font_size_cdlp,
      'buttons': editableData.font_size_buttons,
      'kuroneko_logo': editableData.font_size_kuroneko_logo,
      'genre': editableData.font_size_genre,
      'marketing': editableData.font_size_marketing
    };
    
    const customSize = fontSizeMapping[element];
    return customSize && customSize !== '' ? customSize : defaultSize;
  };
  
  // Helper pour obtenir la taille de police en pixels
  const getFontSizeInPx = (element, defaultPx = '14px') => {
    const customSize = getCustomFontSize(element, '');
    
    // Si pas de taille personnalisée, retourner la valeur par défaut
    if (!customSize || customSize === '') return defaultPx;
    
    // Si c'est déjà une valeur en pixels (ex: "11px"), la retourner directement
    if (customSize.endsWith('px')) {
      return customSize;
    }
    
    // Extraire les pixels des classes text-[Xpx]
    const match = customSize.match(/text-\[(\d+)px\]/);
    if (match) {
      return `${match[1]}px`;
    }
    
    // Mapping des classes Tailwind standards vers pixels
    const tailwindToPixels = {
      'text-xs': '12px',
      'text-sm': '14px',
      'text-base': '16px',
      'text-lg': '18px',
      'text-xl': '20px',
      'text-2xl': '24px',
      'text-3xl': '30px',
      'text-4xl': '36px',
      'text-5xl': '48px'
    };
    
    return tailwindToPixels[customSize] || defaultPx;
  };
  
  // Helper pour obtenir la police d'écriture personnalisée
  const getFontFamily = (element, defaultFont = 'Arial, sans-serif') => {
    const fontFamilyMapping = {
      'tracklist': editableData.font_family_tracklist,
      'argumentaire': editableData.font_family_argumentaire,
      'catalog': editableData.font_family_catalog,
      'artist_title': editableData.font_family_artist_title,
      'promotion': editableData.font_family_promotion,
      'concert': editableData.font_family_concert,
      'cdlp': editableData.font_family_cdlp,
      'buttons': editableData.font_family_buttons,
      'kuroneko_logo': editableData.font_family_kuroneko_logo,
      'genre': editableData.font_family_genre,
      'marketing': editableData.font_family_marketing
    };
    
    const customFont = fontFamilyMapping[element];
    if (customFont && customFont !== '' && customFont !== 'default') {
      return customFont;
    }
    return defaultFont;
  };

  // Get clip button color classes
  const getClipButtonColorClasses = () => {
    const color = editableData.clip_button_color || 'red';
    const colorMap = {
      red: 'bg-gradient-to-r from-red-600 to-pink-600',
      blue: 'bg-gradient-to-r from-blue-600 to-cyan-600',
      green: 'bg-gradient-to-r from-green-600 to-emerald-600',
      purple: 'bg-gradient-to-r from-purple-600 to-fuchsia-600',
      orange: 'bg-gradient-to-r from-orange-600 to-amber-600'
    };
    return colorMap[color] || colorMap.red;
  };

  // Specific font size getters for each element
  // IMPORTANT: Ces fonctions utilisent le système adaptatif pour ajuster automatiquement
  // les tailles de police selon le contenu
  const getTracklistFontSize = () => {
    // Si custom font size défini, l'utiliser, sinon utiliser l'adaptatif
    const customSize = getCustomFontSize('tracklist', '');
    return customSize !== '' ? customSize : getAdaptiveTracklistFontSize();
  };
  
  const getConcertFontSize = () => {
    const customSize = getCustomFontSize('concert', '');
    return customSize !== '' ? customSize : getAdaptiveConcertFontSize();
  };
  
  // Ces fonctions gardent le comportement custom uniquement (pas d'adaptation automatique)
  const getArgumentaireFontSize = () => getCustomFontSize('argumentaire', 'text-base');
  const getCatalogFontSize = () => getCustomFontSize('catalog', 'text-lg');
  const getArtistTitleFontSize = () => getCustomFontSize('artist_title', 'text-3xl');
  const getPromotionFontSize = () => getCustomFontSize('promotion', 'text-sm');
  
  // Fonction pour obtenir la taille CD/LP personnalisée avec fallback sur les valeurs par défaut
  const getCDLPFontSize = (isSingleFormat, formatCount, textType = 'normal') => {
    const customSize = editableData.font_size_cdlp;
    if (customSize && customSize !== '') {
      // Si une taille personnalisée est définie, l'utiliser en pixels
      return getFontSizeInPx('cdlp', '10px');
    }
    // Sinon utiliser les valeurs par défaut adaptatives
    if (textType === 'price') {
      return isSingleFormat ? '10px' : (formatCount === 2 ? '9px' : '8px');
    }
    return isSingleFormat ? '11px' : (formatCount === 2 ? '10px' : '9px');
  };
  
  // Calculer combien de titres peuvent rentrer dans le conteneur de 132mm
  const getMaxVisibleTracks = () => {
    // Référence : 17 titres rentrent à minHeight 20px → 340px d'espace utile
    const availableHeightPx = 340;
    const fontSizeStr = getFontSizeInPx('tracklist', '14px');
    const fontSize = parseInt(fontSizeStr) || 14;
    // Hauteur par ligne = taille police + 6px (padding/border)
    // À 14px → 20px par ligne → 17 titres (référence exacte)
    const lineHeight = fontSize + 6;
    return Math.max(5, Math.floor(availableHeightPx / lineHeight));
  };
  
  // ===== Modèle FR / Export : libellés de la fiche =====
  const isExport = () => editableData.sheet_language === 'EN';
  const SHEET_LABELS = {
    ecouter: { FR: 'ÉCOUTER', EN: 'LISTEN' },
    clip: { FR: 'CLIP OFFICIEL', EN: 'OFFICIAL VIDEO' },
    concert: { FR: 'EN CONCERT', EN: 'ON TOUR' },
    marketing: { FR: 'MARKETING & RÉSEAUX', EN: 'MARKETING & SOCIAL' },
    etAussi: { FR: 'Et aussi :', EN: 'Also :' },
    preco: { FR: 'Fin de préco le', EN: 'Pre-order until' },
    sortie: { FR: 'Sortie le', EN: 'Release date' },
    seeAll: { FR: 'Voir la liste des concerts', EN: 'See all tour dates' }
  };
  const t = (key) => SHEET_LABELS[key][isExport() ? 'EN' : 'FR'];

  // Taille de police des boutons (le bloc pochette est réduit à scale(0.45) :
  // on compense pour que la valeur en px corresponde au rendu visuel réel)
  const getButtonFontSize = () => {
    const px = parseInt(getFontSizeInPx('buttons', '10px')) || 10;
    return `${(px / 0.45).toFixed(1)}px`;
  };

  // Hauteur du cadre pochette+boutons : 83/76mm de base, étendue si l'utilisateur
  // agrandit la police des boutons (Étape 6) pour éviter toute troncature
  const getCoverBlockHeight = () => {
    const base = editableData.enable_clip ? '83mm' : '76mm';
    const actualFont = parseFloat(getButtonFontSize());
    const buttonHeight = Math.max(40, 8 + actualFont * 1.3);
    const buttonCount = (editableData.ecouter_link ? 1 : 0) + (editableData.enable_clip && editableData.clip_link ? 1 : 0);
    const extra = Math.round((buttonHeight - 40) * 0.45 * buttonCount);
    return extra > 0 ? `calc(${base} + ${extra}px)` : base;
  };

  // Hauteur d'une ligne blanche : suit la taille de police choisie (Étape 6)
  const getRowHeight = (element, defaultPx) => {
    const px = parseInt(getFontSizeInPx(element, defaultPx)) || parseInt(defaultPx);
    return `${Math.round(px * 2.67) + 4}px`;
  };

  // Nom du genre affiché : version anglaise en modèle Export
  const getGenreLabel = () => {
    if (!isExport()) return editableData.genre || '';
    const match = genresList.find(g => g.name === editableData.genre);
    return (match && match.name_en) || editableData.genre || '';
  };

  // Taille des logos médias/réseaux : proportionnelle à la taille de police
  // (24px pour la police par défaut de 9px) afin de libérer de la place
  const getMediaIconSize = (element, defaultPx = '9px') => {
    const px = parseInt(getFontSizeInPx(element, defaultPx)) || parseInt(defaultPx);
    return `${Math.max(12, Math.round(px * 2.67))}px`;
  };

  // Helper pour obtenir la taille du logo Exclus disquaires
  const getExcluDisquairesSize = (isSingleFormat) => {
    const customSize = editableData.size_exclu_disquaires;
    if (customSize && customSize !== 'default' && customSize !== '') {
      // customSize contient déjà "px" (ex: "40px")
      return customSize;
    }
    return isSingleFormat ? '90px' : '70px';  // Augmenté : 90px (single) et 70px (multi)
  };
  
  // Helper pour obtenir la taille du code-barres
  const getBarcodeSize = (isSingleFormat) => {
    const customSize = editableData.size_barcode;
    if (customSize && customSize !== 'default' && customSize !== '') {
      // customSize contient déjà "px" (ex: "60px")
      return customSize;
    }
    return isSingleFormat ? '80px' : '60px';
  };

  // Auto-layout optimization for print
  const optimizeLayout = () => {
    if (autoLayoutMode === 'print-optimized') {
      return 'print-optimized';
    }
    return autoLayoutMode;
  };

  // Template mode for different layouts
  const getTemplateClasses = () => {
    if (templateMode === 'landscape') {
      return {
        container: 'grid grid-cols-12 gap-6',
        leftColumn: 'col-span-3',    // Album cover + essential info
        centerColumn: 'col-span-5',  // Argumentaire + Tracklist
        rightColumn: 'col-span-4'    // Formats + Media + Concerts
      };
    }
    // Default portrait mode
    const layout = optimizeLayout();
    switch (layout) {
      case 'compact':
        return {
          container: 'grid grid-cols-12 gap-2',
          formats: 'col-span-4',
          media: 'col-span-4', 
          tracklist: 'col-span-4'
        };
      case 'print-optimized':
        return {
          container: 'grid grid-cols-12 gap-3',
          formats: 'col-span-3',
          media: 'col-span-6',
          tracklist: 'col-span-3'
        };
      default:
        return {
          container: 'grid grid-cols-12 gap-4',
          formats: 'col-span-3',
          media: 'col-span-6',
          tracklist: 'col-span-3'
        };
    }
  };

  // Alias for backward compatibility
  const getLayoutClasses = getTemplateClasses;

  // Load media filenames for correct icon display
  useEffect(() => {
    const loadMediaFilenames = async () => {
      const filenames = {};
      for (const media of editableData.selected_media.concat((editableData.enable_et_aussi && editableData.et_aussi_medias) || [])) {
        try {
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/media-filename/${encodeURIComponent(media)}`);
          if (response.ok) {
            const data = await response.json();
            filenames[media] = data.filename;
          } else {
            // Fallback to old logic
            filenames[media] = `${media.toLowerCase().replace(/\s+/g, '_').replace(/&/g, 'and')}.png`;
          }
        } catch (error) {
          // Fallback to old logic
          filenames[media] = `${media.toLowerCase().replace(/\s+/g, '_').replace(/&/g, 'and')}.png`;
        }
      }
      setMediaFilenames(filenames);
    };

    if (editableData.selected_media.length > 0 || (editableData.enable_et_aussi && editableData.et_aussi_medias && editableData.et_aussi_medias.length > 0)) {
      loadMediaFilenames();
    }
  }, [editableData.selected_media, editableData.et_aussi_medias, editableData.enable_et_aussi]); // Default layout order

  // Convert images to base64 for PDF export compatibility
  const [base64Images, setBase64Images] = useState({});

  useEffect(() => {
    const convertImagesToBase64 = async () => {
      const converted = {};
      
      // Convert main images
      for (const [key, file] of Object.entries(images)) {
        if (file) {
          try {
            const base64 = await fileToBase64(file);
            converted[key] = base64;
          } catch (error) {
            console.error(`Error converting ${key} to base64:`, error);
          }
        }
      }
      
      // Convert format barcodes
      if (editableData.formats) {
        for (let i = 0; i < editableData.formats.length; i++) {
          const format = editableData.formats[i];
          if (format.code_barre_image) {
            try {
              const base64 = await fileToBase64(format.code_barre_image);
              converted[`barcode_${i}`] = base64;
            } catch (error) {
              console.error(`Error converting barcode ${i} to base64:`, error);
            }
          }
        }
      }
      
      setBase64Images(converted);
    };

    convertImagesToBase64();
  }, [images, editableData.formats]);

  // Genre-based color themes - Couleurs officielles Kuroneko
  const getThemeFromGenre = (genre) => {
    // Couleurs officielles Kuroneko
    const KURONEKO_COLORS = {
      rose: '#FF33CC',
      jaune: '#CC9900',
      violet: '#9900CC',
      bleu: '#0A29C2',
      rouge: '#FF0000',
      orange: '#E97132',
      vert: '#196B24'
    };
    
    const genreColorMap = {
      'BLUES': { primary: KURONEKO_COLORS.rose, secondary: '#CC2999', accent: KURONEKO_COLORS.rose, bg: '#fdf2f8' },
      'SOUL / FUNK': { primary: KURONEKO_COLORS.rose, secondary: '#CC2999', accent: KURONEKO_COLORS.rose, bg: '#fdf2f8' },
      'SOUL': { primary: KURONEKO_COLORS.rose, secondary: '#CC2999', accent: KURONEKO_COLORS.rose, bg: '#fdf2f8' },
      'FUNK': { primary: KURONEKO_COLORS.rose, secondary: '#CC2999', accent: KURONEKO_COLORS.rose, bg: '#fdf2f8' },
      'COUNTRY': { primary: KURONEKO_COLORS.rose, secondary: '#CC2999', accent: KURONEKO_COLORS.rose, bg: '#fdf2f8' },
      'CLASSIQUE': { primary: KURONEKO_COLORS.jaune, secondary: '#996600', accent: KURONEKO_COLORS.jaune, bg: '#fffbeb' },
      'BO': { primary: KURONEKO_COLORS.violet, secondary: '#660099', accent: KURONEKO_COLORS.violet, bg: '#faf5ff' },
      'RELIGIEUX': { primary: KURONEKO_COLORS.violet, secondary: '#660099', accent: KURONEKO_COLORS.violet, bg: '#faf5ff' },
      'RELAXATION': { primary: KURONEKO_COLORS.violet, secondary: '#660099', accent: KURONEKO_COLORS.violet, bg: '#faf5ff' },
      'JEUNESSE': { primary: KURONEKO_COLORS.violet, secondary: '#660099', accent: KURONEKO_COLORS.violet, bg: '#faf5ff' },
      'LIVRE AUDIO': { primary: KURONEKO_COLORS.violet, secondary: '#660099', accent: KURONEKO_COLORS.violet, bg: '#faf5ff' },
      'SPECTACLE': { primary: KURONEKO_COLORS.violet, secondary: '#660099', accent: KURONEKO_COLORS.violet, bg: '#faf5ff' },
      'ELECTRO': { primary: KURONEKO_COLORS.bleu, secondary: '#061A8A', accent: KURONEKO_COLORS.bleu, bg: '#eff6ff' },
      'ROCK': { primary: KURONEKO_COLORS.rouge, secondary: '#CC0000', accent: KURONEKO_COLORS.rouge, bg: '#fef2f2' },
      'METAL': { primary: KURONEKO_COLORS.rouge, secondary: '#CC0000', accent: KURONEKO_COLORS.rouge, bg: '#fef2f2' },
      'POP': { primary: KURONEKO_COLORS.orange, secondary: '#C45A1F', accent: KURONEKO_COLORS.orange, bg: '#fff7ed' },
      'VF': { primary: KURONEKO_COLORS.orange, secondary: '#C45A1F', accent: KURONEKO_COLORS.orange, bg: '#fff7ed' },
      'INDIE': { primary: KURONEKO_COLORS.orange, secondary: '#C45A1F', accent: KURONEKO_COLORS.orange, bg: '#fff7ed' },
      'HIP-HOP': { primary: KURONEKO_COLORS.vert, secondary: '#0F4716', accent: KURONEKO_COLORS.vert, bg: '#f0fdf4' },
      'REGGAE': { primary: KURONEKO_COLORS.vert, secondary: '#0F4716', accent: KURONEKO_COLORS.vert, bg: '#f0fdf4' },
      'WORLD': { primary: KURONEKO_COLORS.vert, secondary: '#0F4716', accent: KURONEKO_COLORS.vert, bg: '#f0fdf4' }
    };
    
    // Find the matching genre (case insensitive)
    const normalizedGenre = genre.toUpperCase();
    if (genreColorMap[normalizedGenre]) {
      return genreColorMap[normalizedGenre];
    }
    
    // Try partial match
    for (const [key, colors] of Object.entries(genreColorMap)) {
      if (normalizedGenre.includes(key) || key.includes(normalizedGenre)) {
        return colors;
      }
    }
    
    // Default theme if no match - use Kuroneko green
    return { primary: '#4DBA5F', secondary: '#3A9149', accent: '#4DBA5F', bg: '#f8fafc' };
  };

  // Kuroneko default theme (override genre colors for template)
  const kuronekotheme = {
    primary: '#4DBA5F', // Kuroneko Green
    secondary: '#1F2937', // Dark gray  
    accent: '#4DBA5F', // Kuroneko Green
    bg: '#111827' // Dark background
  };

  const theme = getThemeFromGenre(editableData.genre || 'DEFAULT');

  const handleEdit = (field, value) => {
    setEditableData(prev => ({ ...prev, [field]: value }));
  };

  // Function to format argumentaire with automatic detection
  const formatArgumentaire = (text) => {
    if (!text) return text;
    
    let formattedText = text;
    
    // Replace track titles with quoted versions (only this formatting is kept automatic)
    if (editableData.tracks && editableData.tracks.length > 0) {
      editableData.tracks.forEach(track => {
        if (track && track.length > 2) { // Only for meaningful track names
          // Nettoyer le track des numéros "1. ", "2. " etc si présents
          const cleanTrack = track.replace(/^\d+\.\s*/, '');
          if (cleanTrack.length > 2) {
            const trackRegex = new RegExp(`\\b${cleanTrack.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'gi');
            formattedText = formattedText.replace(trackRegex, `"${cleanTrack}"`);
          }
        }
      });
    }
    
    // User can manually add **text** for bold or *text* for italic
    // These will be processed by renderFormattedText
    
    return formattedText;
  };

  // Function to render formatted text (markdown-like)
  const renderFormattedText = (text) => {
    if (!text) return text;
    
    // Convert **text** to bold
    let result = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    
    // Convert *text* to italic
    result = result.replace(/\*(.*?)\*/g, '<em>$1</em>');
    
    // Convert _text_ to underline
    result = result.replace(/_(.*?)_/g, '<span style="text-decoration: underline;">$1</span>');
    
    // Ne plus convertir les \n en <br> - ils sont ignorés
    // result = result.replace(/\n/g, '<br style="line-height: 0.8; margin: 0;" />');
    
    // Convert "text" to quotes (already quoted, just keep them)
    
    return <span dangerouslySetInnerHTML={{ __html: result }} />;
  };
  
  // Drag and drop handlers
  const handleDragStart = (id) => {
    console.log('Drag started:', id);
  };

  const handleDrop = (draggedId, targetId) => {
    if (draggedId !== targetId) {
      const oldIndex = layouts.indexOf(draggedId);
      const newIndex = layouts.indexOf(targetId);
      
      if (oldIndex !== -1 && newIndex !== -1) {
        const newLayouts = [...layouts];
        newLayouts[oldIndex] = targetId;
        newLayouts[newIndex] = draggedId;
        setLayouts(newLayouts);
        console.log('Layout changed:', newLayouts);
      }
    }
  };

  // Layout management
  const handleLayoutChange = (newLayouts) => {
    setLayouts(newLayouts);
  };

  // Advanced editing functions
  const removeTrack = (index) => {
    const newTracks = editableData.tracks.filter((_, i) => i !== index);
    setEditableData(prev => ({ ...prev, tracks: newTracks }));
  };
  
  const editTrack = (index, newValue) => {
    const newTracks = [...editableData.tracks];
    newTracks[index] = newValue;
    setEditableData(prev => ({ ...prev, tracks: newTracks }));
  };
  
  const addTrack = () => {
    const newTracks = [...editableData.tracks, "Nouveau titre"];
    setEditableData(prev => ({ ...prev, tracks: newTracks }));
  };

  // Calculate dynamic font size for tracklist based on number of tracks
  const getTrackFontSize = (trackCount) => {
    if (trackCount <= 17) {
      return 'text-xs'; // Normal size for 17 or less tracks
    }
    // Reduce font size proportionally for more than 17 tracks
    if (trackCount <= 25) {
      return 'text-[10px]'; // Smaller for 18-25 tracks
    }
    if (trackCount <= 35) {
      return 'text-[9px]'; // Even smaller for 26-35 tracks
    }
    return 'text-[8px]'; // Minimum size for 36+ tracks
  };

  // Calculate container padding based on track count
  const getTrackContainerClasses = (trackCount) => {
    if (trackCount <= 17) {
      return 'space-y-1'; // Normal spacing
    }
    if (trackCount <= 25) {
      return 'space-y-0.5'; // Reduced spacing
    }
    return 'space-y-0'; // Minimal spacing for many tracks
  };
  
  const removeFormat = (index) => {
    const newFormats = editableData.formats.filter((_, i) => i !== index);
    setEditableData(prev => ({ ...prev, formats: newFormats }));
  };
  
  const removeConcert = (index) => {
    const newConcerts = editableData.concerts.filter((_, i) => i !== index);
    setEditableData(prev => ({ ...prev, concerts: newConcerts }));
  };
  
  const removeMedia = (mediaName) => {
    const newSelectedMedia = editableData.selected_media.filter(m => m !== mediaName);
    const newDescriptions = { ...editableData.media_descriptions };
    delete newDescriptions[mediaName];
    setEditableData(prev => ({ 
      ...prev, 
      selected_media: newSelectedMedia,
      media_descriptions: newDescriptions
    }));
  };
  
  // New Landscape Components
  // Nouveau composant : Pochette + Boutons
  const LandscapeCoverAndButtons = () => (
    <div className="flex flex-col gap-2 print-pochette-container">
      {/* Album Cover - IMAGE COMPLÈTE NON TRONQUÉE */}
      <div 
        className="relative w-full mx-auto rounded-lg overflow-hidden shadow-lg bg-gradient-to-br from-gray-100 to-gray-200 border-2 border-gray-300 print-pochette-wrapper"
        style={{ aspectRatio: '1' }}
      >
          {base64Images.cover_image ? (
            <img 
              src={base64Images.cover_image}
              alt="Album Cover"
              className="w-full h-full object-contain print-pochette-image"
              style={{ display: 'block', maxWidth: '100%', maxHeight: '100%' }}
            />
          ) : images.cover_image ? (
            <img 
              src={URL.createObjectURL(images.cover_image)}
              alt="Album Cover"
              className="w-full h-full object-contain print-pochette-image"
              style={{ display: 'block', maxWidth: '100%', maxHeight: '100%' }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-400">
              <div className="text-center">
                <div className="text-2xl mb-1">🎵</div>
                <div className="text-[8px] font-medium">Cover</div>
              </div>
            </div>
          )}
        </div>

      {/* Action Buttons - AGRANDIS (1.5x hauteur) */}
      <div className="space-y-2">
        {/* Listen Button */}
        {editableData.ecouter_link && (
          <a 
              href={editableData.ecouter_link}
              target="_blank"
              rel="noopener noreferrer"
              className="button-export flex items-center justify-center w-full py-1 px-3 rounded-md text-white font-bold text-center shadow-md cursor-pointer hover:opacity-90"
              style={{ backgroundColor: '#439C4A', minHeight: '40px', fontSize: getButtonFontSize(), fontFamily: getFontFamily('buttons', 'Arial, sans-serif') }}
            >
              <span>{t('ecouter')}</span>
            </a>
          
        )}

        {/* YouTube Clip Button - SANS ICÔNE */}
        {editableData.enable_clip && editableData.clip_link && (
          <a 
              href={editableData.clip_link}
              target="_blank"
              rel="noopener noreferrer"
              className={`button-export flex items-center justify-center w-full py-1 px-3 rounded-md text-white font-bold text-center shadow-md cursor-pointer hover:opacity-90 ${editableData.clip_color ? '' : getClipButtonColorClasses()}`}
              style={{ minHeight: '40px', fontSize: getButtonFontSize(), fontFamily: getFontFamily('buttons', 'Arial, sans-serif'), ...(editableData.clip_color ? { background: editableData.clip_color } : {}) }}
            >
              <span>{t('clip')}</span>
            </a>
          
        )}
      </div>
    </div>
  );

  // Composant : Bloc PC EXPRESS séparé - DATES SUR MÊME LIGNE - SANS BORDURE - ESPACE 1MM
  const PCExpressBlock = () => (
    <div className="bg-white rounded" style={{ padding: '0.5mm' }}>
      <div className="text-center space-y-1">
        {/* PC Type and Express Title */}
        {(editableData.pc_type || editableData.pc_expres) && (
          <div 
            className="w-full py-1 px-2 rounded text-center text-white font-bold text-[9px]"
            style={{ backgroundColor: kuronekotheme.primary }}
          >
            {editableData.pc_type && <span>{editableData.pc_type}</span>}
            {editableData.pc_type && editableData.pc_expres && <span> - </span>}
            {editableData.pc_expres && <span>{editableData.pc_expres}</span>}
          </div>
        )}
        
        {/* General dates - SUR LA MÊME LIGNE */}
        {(editableData.date_de_sortie || editableData.fin_de_preco) && (
          <div className="flex gap-1 text-[8px] text-gray-700">
            {editableData.fin_de_preco && (
              <div 
                className="px-1 py-0.5 rounded font-medium flex-1"
                style={{
                  backgroundColor: editableData.date_preco_color 
                    ? `${editableData.date_preco_color}20` 
                    : '#fed7aa',
                  color: editableData.date_preco_color || '#9a3412'
                }}
              >
                {t('preco')} {formatConcertDate(editableData.fin_de_preco)}
              </div>
            )}
            {editableData.date_de_sortie && (
              <div 
                className="px-1 py-0.5 rounded font-medium flex-1"
                style={{
                  backgroundColor: editableData.date_sortie_color 
                    ? `${editableData.date_sortie_color}20` 
                    : '#bfdbfe',
                  color: editableData.date_sortie_color || '#1e40af'
                }}
              >
                {t('sortie')} {formatConcertDate(editableData.date_de_sortie)}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  // Nouveau composant : CD/LP UNIQUEMENT (sans PC Express) - MAX 4 FORMATS
  const LandscapeLeftColumn = () => {
    const formatCount = editableData.formats?.slice(0, 4).length || 1;
    // Hauteur dynamique selon nombre de formats
    const getFormatHeight = () => {
      if (formatCount === 1) return '100%';
      if (formatCount === 2) return 'calc(50% - 0.25mm)';
      if (formatCount === 3) return '80px';
      return '60px'; // 4 formats = hauteur réduite
    };
    
    return (
    <div className="flex flex-col gap-0.5" style={{ width: '100%', height: '100%' }}>
      {/* CD/LP Formats */}
      {editableData.formats?.slice(0, 4).map((format, index) => {
        const isSingleFormat = formatCount === 1;
        
        return (
        <div key={index} className="bg-white rounded shadow-sm border border-gray-100" style={{ height: getFormatHeight(), width: '100%' }}>
            {/* Encadré vert - LAYOUT CONDITIONNEL : Vertical si 1 format, Horizontal si 2-3 */}
            <div className="border rounded p-1 flex" style={{ 
              borderColor: kuronekotheme.primary, 
              borderWidth: '1px', 
              height: '100%', 
              gap: '2mm',
              flexDirection: isSingleFormat ? 'column' : 'row'
            }}>
              {/* PARTIE INFOS */}
              <div className="flex flex-col justify-between" style={{ width: isSingleFormat ? '100%' : '58%' }}>
                {/* 3 lignes de texte + Logo Exclu */}
                <div className="flex justify-between items-start">
                  <div className="flex-1 text-left space-y-0.5">
                    {/* 1. CD/LP (Optionnel infos) - TAILLE ADAPTATIVE */}
                    <div className="font-bold text-black" style={{ 
                      fontSize: getCDLPFontSize(isSingleFormat, formatCount, 'normal'),
                      fontFamily: getFontFamily('cdlp', 'Arial, sans-serif')
                    }}>
                      {format.type.toUpperCase()}
                      {format.text && <span className="font-normal text-gray-600" style={{ 
                        fontSize: getCDLPFontSize(isSingleFormat, formatCount, 'normal'), 
                        fontFamily: getFontFamily('cdlp', 'Arial, sans-serif'),
                        marginLeft: '2px' 
                      }}>({format.text})</span>}
                    </div>
                    
                    {/* 2. Numéro de catalogue - TAILLE ADAPTATIVE */}
                    {format.num_catalog && (
                      <div className="text-black" style={{ 
                        fontSize: getCDLPFontSize(isSingleFormat, formatCount, 'normal'),
                        fontFamily: getFontFamily('cdlp', 'Arial, sans-serif')
                      }}>
                        {format.num_catalog}
                      </div>
                    )}
                    
                    {/* 3. Prix en euros avec code KUR - TAILLE ADAPTATIVE */}
                    {format.prix && !isExport() && (
                      <div className="text-black" style={{ 
                        fontSize: getCDLPFontSize(isSingleFormat, formatCount, 'price'),
                        fontFamily: getFontFamily('cdlp', 'Arial, sans-serif')
                      }}>
                        {formatPriceDisplay(format.prix)}
                      </div>
                    )}
                  </div>

                  {/* LOGO EXCLU à côté des 3 lignes - ADAPTATIF selon nombre de formats */}
                  {format.exclu_disquaires && (
                    <div style={{ 
                      width: getExcluDisquairesSize(isSingleFormat),
                      alignSelf: 'flex-end', 
                      marginTop: isSingleFormat ? '8px' : '1px', 
                      marginLeft: isSingleFormat ? '2px' : '4px'
                    }}>
                      <img 
                        src={`${process.env.REACT_APP_BACKEND_URL}/api/exclu_disquaires_indes.png`}
                        alt="Exclu disquaires indés"
                        className="w-full h-auto object-contain"
                        style={{ 
                          maxHeight: getExcluDisquairesSize(isSingleFormat),
                          imageRendering: '-webkit-optimize-contrast',
                          WebkitFontSmoothing: 'antialiased'
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'block';
                        }}
                      />
                      <div 
                        className="badge px-0.5 py-0 rounded text-[8px] font-bold text-white text-center"
                        style={{ backgroundColor: kuronekotheme.primary, display: 'none' }}
                      >
                        EXCLU
                      </div>
                    </div>
                  )}
                </div>
                
                {/* 4. Fin préco/Date de sortie (Optionnel) - En bas à gauche - TAILLE ADAPTATIVE */}
                {(format.preorder_end_date || format.release_date) && (
                  <div className="flex gap-0.5 text-gray-600 mt-auto" style={{ 
                    fontSize: isSingleFormat ? '10px' : (formatCount === 2 ? '9px' : '8px') 
                  }}>
                    {format.preorder_end_date && (
                      <div className="bg-orange-100 px-0.5 py-0 rounded text-orange-800">
                        {formatConcertDate(format.preorder_end_date)}
                      </div>
                    )}
                    {format.release_date && (
                      <div className="bg-blue-100 px-0.5 py-0 rounded text-blue-800">
                        {formatConcertDate(format.release_date)}
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* PARTIE CODE BARRE - Conditionnel selon nombre de formats */}
              <div className="flex items-center justify-center" style={{ 
                width: isSingleFormat ? '100%' : '42%',
                marginTop: isSingleFormat ? '2mm' : '0'
              }}>
                <div className="p-1 bg-white border border-gray-300 rounded flex items-center justify-center" style={{ 
                  width: '100%',
                  height: isSingleFormat ? 'auto' : '100%'
                }}>
                  {format.code_barre_image ? (
                    <img 
                      src={URL.createObjectURL(format.code_barre_image)} 
                      alt="Code barre" 
                      className="w-full h-auto object-contain"
                      style={{ maxHeight: getBarcodeSize(isSingleFormat) }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400 text-[6px]">
                      Code barre
                    </div>
                  )}
                </div>
              </div>
            </div>
        </div>
        );
      })}
    </div>
    );
  };

  const LandscapeCenterColumn = () => (
    <div className="h-full flex flex-col">
      {/* Tracklist - SANS LIMITATION DE HAUTEUR */}
      <div className="flex flex-col flex-1">
        <div 
          className="rounded p-1 shadow-sm"
          style={{ 
            backgroundColor: `${kuronekotheme.primary}15`,
            height: '100%',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <div className="flex justify-between items-center mb-0.5">
            <h3 className="text-[10px] font-bold" style={{ color: kuronekotheme.primary }}>
              TRACKLIST
            </h3>
            {advancedEdit && (
              <Button
                onClick={addTrack}
                size="sm"
                className="h-4 px-1 text-[8px]"
                style={{ backgroundColor: kuronekotheme.primary }}
              >
                +
              </Button>
            )}
          </div>
          
          {/* All tracks visible - DYNAMIQUE selon hauteur disponible */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0px', flex: 1 }}>
            {editableData.tracks.slice(0, getMaxVisibleTracks()).map((track, index) => {
              const maxVisible = getMaxVisibleTracks();
              const isLastVisible = index === maxVisible - 1;
              const hasMoreTracks = editableData.tracks.length > maxVisible;
              
              return (
              <div key={index} className="w-full" style={{ marginBottom: '0px' }}>
                <div className="bg-white p-0.5 rounded shadow-sm group" style={{ minHeight: `${(parseInt(getFontSizeInPx('tracklist', '14px')) || 14) + 6}px`, borderBottom: index < maxVisible - 1 ? '1px solid #e5e7eb' : 'none' }}>
                  <div className="flex items-center space-x-1" style={{ minHeight: `${(parseInt(getFontSizeInPx('tracklist', '14px')) || 14) + 6}px` }}>
                    <span 
                      className="font-bold flex-shrink-0"
                      style={{ color: '#4DBA5F', fontSize: getFontSizeInPx('tracklist', '14px'), minWidth: '20px', lineHeight: '1.2' }}
                    >
                      {index + 1}.
                    </span>
                    {advancedEdit ? (
                      <Input
                        value={track}
                        onChange={(e) => editTrack(index, e.target.value)}
                        className={`${getTracklistFontSize()} h-4 flex-1 border-none bg-transparent p-0`}
                      />
                    ) : (
                      <span className="font-medium flex-1" style={{ fontFamily: getFontFamily('tracklist', 'Arial, sans-serif'), fontSize: getFontSizeInPx('tracklist', '14px'), color: '#374151', lineHeight: '1.2' }}>
                        {renderFormattedText(track)}
                        {/* Afficher les "..." sur le dernier titre visible s'il y a plus de titres */}
                        {isLastVisible && hasMoreTracks && (
                          <span style={{ color: '#4DBA5F', fontWeight: 'bold', marginLeft: '4px', fontSize: '18px' }}>...</span>
                        )}
                      </span>
                    )}
                    {advancedEdit && (
                      <Button
                        onClick={() => removeTrack(index)}
                        size="sm"
                        variant="ghost"
                        className="h-4 w-4 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Trash2 className="h-2 w-2 text-red-500" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );

  const LandscapeRightColumn = () => (
    <div className="flex flex-col h-full">
      {/* Media Mentions - Promotion section ULTRA COMPACT */}
      {(editableData.selected_media.length > 0 || (editableData.enable_et_aussi && editableData.et_aussi_medias && editableData.et_aussi_medias.length > 0)) && (
        <div 
            className="rounded-lg p-1.5 shadow-sm"
            style={{ backgroundColor: `${kuronekotheme.primary}15` }}
          >
            <h3 
              className="font-bold text-[10px] mb-1 text-left"
              style={{ color: kuronekotheme.primary }}
            >
              PROMOTION
            </h3>
            
            {/* Regular Promotion Media */}
            {editableData.selected_media.length > 0 && (
              <div className="space-y-0 mb-1">
                {editableData.selected_media.map((media, index) => (
                  <div key={index} className="flex items-start gap-1 bg-white p-0.5 rounded shadow-sm" style={{ minHeight: getRowHeight('promotion', '9px'), borderBottom: index < editableData.selected_media.length - 1 ? '1px solid #e5e7eb' : 'none', marginBottom: '1px' }}>
                    <img 
                      src={`${process.env.REACT_APP_BACKEND_URL}/api/media-icons/${mediaFilenames[media] || `${media.toLowerCase().replace(/\s+/g, '_').replace(/&/g, 'and')}.png`}`}
                      alt={media}
                      className="object-contain mt-0.5 flex-shrink-0"
                      style={{ width: getMediaIconSize('promotion'), height: getMediaIconSize('promotion') }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                    <div 
                      className="w-1 h-1 rounded-full mt-1 flex-shrink-0"
                      style={{ backgroundColor: kuronekotheme.primary, display: 'none' }}
                    ></div>
                    <div className="flex-1">
                      <span style={{ fontFamily: 'Arial, sans-serif', fontSize: getFontSizeInPx('promotion', '9px'), color: 'black', lineHeight: '0.95', margin: 0, padding: 0 }}>
                        <span className="font-bold">{media}</span>
                        {editableData.media_descriptions[media] && (
                          <span> - {editableData.media_descriptions[media]}</span>
                        )}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Et aussi - ICÔNES SUR LA MÊME LIGNE - AGRANDI */}
            {editableData.enable_et_aussi && editableData.et_aussi_medias && editableData.et_aussi_medias.length > 0 && (
              <div className="flex items-center gap-1.5">
                <h4 className="text-[11px] font-bold text-left whitespace-nowrap" style={{ color: kuronekotheme.primary }}>{t('etAussi')}</h4>
                <div className="flex gap-1 flex-wrap">
                  {editableData.et_aussi_medias.map((mediaName, index) => (
                    <div key={index} className="w-7 h-7 bg-white rounded shadow-sm flex items-center justify-center border border-gray-200">
                      <img 
                        src={`${process.env.REACT_APP_BACKEND_URL}/api/media-icons/${mediaFilenames[mediaName] || `${mediaName.toLowerCase().replace(/\s+/g, '_').replace(/&/g, 'and')}.png`}`}
                        alt={mediaName}
                        className="w-6 h-6 object-contain"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'block';
                        }}
                      />
                      <div 
                        className="w-6 h-6 bg-gray-100 rounded flex items-center justify-center text-gray-600 font-bold text-[7px]"
                        style={{ display: 'none' }}
                      >
                        {mediaName.substring(0, 2)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        
      )}

      {/* Marketing & Réseaux - STYLE PROMOTION AVEC DESCRIPTIONS */}
      {editableData.enable_marketing && editableData.selected_logos.length > 0 && (
        <div 
            className="rounded-lg p-1.5 shadow-sm"
            style={{ backgroundColor: `${kuronekotheme.primary}15` }}
          >
            <h3 
              className="font-bold text-[10px] mb-1 text-left"
              style={{ color: kuronekotheme.primary }}
            >
              {t('marketing')}
            </h3>
            
            <div className="space-y-0">
              {editableData.selected_logos.slice(0, 4).map((logo, index) => (
                <div key={index} className="flex items-start gap-1 bg-white p-0.5 rounded shadow-sm" style={{ minHeight: getRowHeight('marketing', '9px') }}>
                  <div className="mt-0.5 flex-shrink-0 flex items-center justify-center" style={{ width: getMediaIconSize('marketing'), height: getMediaIconSize('marketing') }}>
                    {getSocialLogo(logo)}
                  </div>
                  <div className="flex-1">
                    <span style={{ fontFamily: getFontFamily('marketing', 'Arial, sans-serif'), fontSize: getFontSizeInPx('marketing', '9px'), color: 'black' }}>
                      <span className="font-bold">{logo}</span>
                      {editableData.social_media_descriptions && editableData.social_media_descriptions[logo] && (
                        <span> - {editableData.social_media_descriptions[logo]}</span>
                      )}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        
      )}

      {/* Concerts - SANS LIMITATION */}
      {editableData.enable_concerts && editableData.concerts.length > 0 && (
        <div 
            className="rounded-lg p-1.5 shadow-sm"
            style={{ 
              backgroundColor: `${kuronekotheme.primary}15`
            }}
          >
            <h3 
              className="font-bold text-[10px] mb-1 text-left"
              style={{ color: kuronekotheme.primary }}
            >
              {t('concert')}
            </h3>
            <div className="space-y-0">
              {editableData.concerts.map((concert, index) => (
                <div key={concert.id || index} className="bg-white p-0.5 rounded shadow-sm text-left" style={{ borderBottom: index < editableData.concerts.length - 1 ? '1px solid #e5e7eb' : 'none', marginBottom: '1px' }}>
                  <div style={{ fontFamily: 'Arial, sans-serif', fontSize: getFontSizeInPx('concert', '10px'), color: 'black', lineHeight: '1.3' }}>
                    <span className="font-bold">{formatConcertDate(concert.date)}</span> - <span className="font-medium">{concert.text}{concert.country_code ? `, ${concert.country_code}` : ''}</span>
                  </div>
                </div>
              ))}
            </div>
            {/* Résumé de tournée : encadré blanc identique aux lignes de dates */}
            {editableData.concert_free_text && (
              <div className="bg-white p-0.5 rounded shadow-sm text-left" style={{ marginTop: '2px' }}>
                <div style={{ fontFamily: 'Arial, sans-serif', fontSize: getFontSizeInPx('concert', '10px'), color: 'black', lineHeight: '1.3' }}>
                  {editableData.concert_free_text}
                </div>
              </div>
            )}
            {/* Lien vers la page publique des concerts, aligné à gauche sous le reste */}
            {editableData.concert_page_id && (
              <div className="mt-1 text-left">
                <a 
                  href={`${window.location.origin}/concerts/${editableData.concert_page_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block font-bold underline"
                  style={{ fontFamily: 'Arial, sans-serif', fontSize: getFontSizeInPx('concert', '10px'), color: kuronekotheme.primary }}
                >
                  {t('seeAll')}
                </a>
              </div>
            )}
          </div>
        
      )}

      {/* Concert section for file mode WITHOUT manual concerts */}
      {editableData.enable_concerts && editableData.concerts.length === 0 && (editableData.concert_free_text || editableData.concert_page_id) && (
        <div 
            className="rounded-lg p-1.5 shadow-sm"
            style={{ backgroundColor: `${kuronekotheme.primary}15` }}
          >
            <h3 className="font-bold text-[10px] mb-1 text-left" style={{ color: kuronekotheme.primary }}>
              {t('concert')}
            </h3>
            {editableData.concert_free_text && (
              <div className="bg-white p-0.5 rounded shadow-sm text-left">
                <div style={{ fontFamily: 'Arial, sans-serif', fontSize: getFontSizeInPx('concert', '10px'), color: 'black', lineHeight: '1.3' }}>
                  {editableData.concert_free_text}
                </div>
              </div>
            )}
            {editableData.concert_page_id && (
              <div className="mt-1 text-left">
                <a 
                  href={`${window.location.origin}/concerts/${editableData.concert_page_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block font-bold underline"
                  style={{ fontFamily: 'Arial, sans-serif', fontSize: getFontSizeInPx('concert', '10px'), color: kuronekotheme.primary }}
                >
                  {t('seeAll')}
                </a>
              </div>
            )}
          </div>
      )}

      {/* Informations supplémentaires */}
      {editableData.enable_et_aussi && editableData.et_aussi && editableData.et_aussi.trim() && (
        <div 
            className="rounded-xl p-3 shadow-lg"
            style={{ backgroundColor: `${kuronekotheme.secondary}15` }}
          >
            <h4 className="text-sm font-bold mb-2 text-left" style={{ color: kuronekotheme.secondary }}>
              📋 INFORMATIONS COMPLÉMENTAIRES
            </h4>
            <div className="bg-white p-2 rounded-lg shadow-sm">
              <p className="text-xs text-black leading-relaxed text-left">
                {editableData.et_aussi}
              </p>
            </div>
          </div>
        
      )}
    </div>
  );
  const FormatsSection = ({ id = "formats", layoutClass = "col-span-3" }) => (
    <DraggableSection 
      id={id} 
      className={`${layoutClass} space-y-3`}
      advancedEdit={advancedEdit}
      onDragStart={handleDragStart}
      onDrop={handleDrop}
    >
      {/* PC Express and General Dates - NEW separate section above first format */}
      {(editableData.pc_type || editableData.pc_expres || editableData.date_de_sortie || editableData.fin_de_preco) && (
        <div className="bg-gray-50 rounded-2xl p-3 shadow-sm border border-gray-200 mb-3">
          <div className="text-center space-y-2">
            {/* PC Type and Express */}
            {(editableData.pc_type || editableData.pc_expres) && (
              <div 
                className="w-full py-2 px-3 rounded text-center text-white text-sm font-bold"
                style={{ backgroundColor: theme.primary }}
              >
                {editableData.pc_type && <span>{editableData.pc_type}</span>}
                {editableData.pc_type && editableData.pc_expres && <span> - </span>}
                {editableData.pc_expres && <span>{editableData.pc_expres}</span>}
              </div>
            )}
            
            {/* General dates */}
            {(editableData.date_de_sortie || editableData.fin_de_preco) && (
              <div className="space-y-1 text-xs text-gray-600 text-left">
                {editableData.fin_de_preco && (
                  <div 
                    className="px-2 py-1 rounded"
                    style={{
                      backgroundColor: editableData.date_preco_color 
                        ? `${editableData.date_preco_color}20` 
                        : '#fed7aa',
                      color: editableData.date_preco_color || '#9a3412'
                    }}
                  >
                    Fin préco: {formatConcertDate(editableData.fin_de_preco)}
                  </div>
                )}
                {editableData.date_de_sortie && (
                  <div 
                    className="px-2 py-1 rounded"
                    style={{
                      backgroundColor: editableData.date_sortie_color 
                        ? `${editableData.date_sortie_color}20` 
                        : '#bfdbfe',
                      color: editableData.date_sortie_color || '#1e40af'
                    }}
                  >
                    Sortie: {formatConcertDate(editableData.date_de_sortie)}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Formats - Enhanced */}
      <div className="space-y-3">
        {editableData.formats?.map((format, index) => (
          <DraggableElement 
            key={format.id || index} 
            id={`format_${index}`} 
            advancedEdit={advancedEdit} 
            resizable={true}
            className="w-full"
          >
            <div className="bg-white rounded-2xl p-2.5 shadow-lg border border-gray-100">
              <div className="text-left space-y-1.5">
                {/* Format Type and Exclus Disquaires at top */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="font-bold text-lg" style={{ color: theme.primary }}>
                      {format.type.toUpperCase()}
                    </span>
                    {format.text && <span className="font-normal text-gray-600 text-sm">({format.text})</span>}
                  </div>
                  
                  {/* Exclus Disquaires Badge - Moved up */}
                  {format.exclu_disquaires && (
                    <div className="px-2 py-1 rounded text-xs font-bold text-white" style={{ backgroundColor: theme.primary }}>
                      EXCLU DISQUAIRES INDÉS
                    </div>
                  )}
                </div>
                
                {/* Catalog Number - More prominent */}
                {format.num_catalog && (
                  <div className={`font-mono bg-gray-200 px-2 py-1 rounded ${getCatalogFontSize()} font-bold text-left`}>
                    {format.num_catalog}
                  </div>
                )}
                
                {/* Dates under catalog number */}
                <div className="space-y-1 text-xs text-gray-600 text-left">
                  {format.preorder_end_date && (
                    <div className="bg-orange-100 px-2 py-1 rounded text-orange-800">
                      Fin préco: {formatConcertDate(format.preorder_end_date)}
                    </div>
                  )}
                  {format.release_date && (
                    <div className="bg-blue-100 px-2 py-1 rounded text-blue-800">
                      Sortie: {formatConcertDate(format.release_date)}
                    </div>
                  )}
                </div>
                
                {/* Price */}
                {format.prix && (
                  <div className="font-bold text-sm text-left" style={{ color: theme.secondary }}>
                    {formatPriceDisplay(format.prix)}
                  </div>
                )}
                
                {/* Barcode */}
                {format.code_barre_image && (
                  <div className="flex justify-left mt-2">
                    <img 
                      src={base64Images[`barcode_${index}`] || URL.createObjectURL(format.code_barre_image)}
                      alt="Code barre" 
                      className="h-10 object-contain"
                      style={{ display: 'block' }}
                    />
                  </div>
                )}
              </div>
            </div>
          </DraggableElement>
        ))}
      </div>
    </DraggableSection>
  );

  const MediaSection = ({ id = "media", layoutClass = "col-span-6" }) => (
    <DraggableSection 
      id={id} 
      className={`${layoutClass} space-y-4`}
      advancedEdit={advancedEdit}
      onDragStart={handleDragStart}
      onDrop={handleDrop}
    >
      {/* Cover and Clip Layout */}
      <div className={`${images.clip_image ? 'grid grid-cols-2 gap-4' : 'flex justify-center'}`}>
        {/* Album Cover */}
        <DraggableElement 
          id="album_cover" 
          advancedEdit={advancedEdit} 
          resizable={true}
          className="text-center"
        >
          <div className={`relative aspect-square ${images.clip_image ? 'max-w-48' : 'max-w-56'} mx-auto rounded-2xl overflow-hidden shadow-xl bg-gradient-to-br from-gray-100 to-gray-200 border-2 border-white`}>
            {images.cover_image ? (
              <img 
                src={URL.createObjectURL(images.cover_image)} 
                alt="Album Cover"
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-400">
                <div className="text-center">
                  <div className="text-3xl mb-2">🎵</div>
                  <div className="text-xs font-medium">Cover</div>
                </div>
              </div>
            )}
            
            {/* Overlay Listen Button */}
            <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2">
              {editableData.ecouter_link ? (
                <a 
                  href={editableData.ecouter_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block px-4 py-2 rounded-full text-white font-bold text-sm shadow-xl cursor-pointer hover:scale-105 transition-all duration-200 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 border-2 border-white"
                >
                  🎧 ÉCOUTER
                </a>
              ) : (
                <div 
                  className="inline-block px-4 py-2 rounded-full text-gray-400 font-bold text-sm shadow-lg bg-gray-200 border-2 border-gray-300 cursor-not-allowed"
                >
                  🎧 ÉCOUTER
                </div>
              )}
            </div>
          </div>
        </DraggableElement>

        {/* Clip - only if exists */}
        {images.clip_image && (
          <DraggableElement 
            id="clip_section" 
            advancedEdit={advancedEdit} 
            resizable={true}
            className="text-center"
          >
            <div className="bg-white rounded-2xl p-3 shadow-lg border border-gray-100">
              {editableData.clip_link ? (
                <a 
                  href={editableData.clip_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  <h3 
                    className="text-sm font-bold mb-2 text-center cursor-pointer hover:underline"
                    style={{ color: theme.accent }}
                  >
                    🎬 CLIP
                  </h3>
                </a>
              ) : (
                <h3 
                  className="text-sm font-bold mb-2 text-center"
                  style={{ color: theme.accent }}
                >
                  🎬 CLIP
                </h3>
              )}
              <div className="relative aspect-video rounded-xl overflow-hidden max-w-32 mx-auto">
                <img 
                  src={URL.createObjectURL(images.clip_image)} 
                  alt="Clip"
                  className="w-full h-full object-cover"
                />
                
                {/* Overlay Watch Button */}
                <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2">
                  {editableData.clip_link ? (
                    <a 
                      href={editableData.clip_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block px-3 py-1.5 rounded-full text-white font-bold text-xs shadow-xl cursor-pointer hover:scale-105 transition-all duration-200 bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-500 hover:to-pink-500 border-2 border-white"
                    >
                      🎬 REGARDER
                    </a>
                  ) : (
                    <div 
                      className="inline-block px-3 py-1.5 rounded-full text-gray-400 font-bold text-xs shadow-lg bg-gray-200 border-2 border-gray-300 cursor-not-allowed"
                    >
                      🎬 REGARDER
                    </div>
                  )}
                </div>
              </div>
            </div>
          </DraggableElement>
        )}
      </div>

      {/* Concerts - if any */}
      {editableData.enable_concerts && editableData.concerts.length > 0 && (
        <DraggableElement 
          id="concerts_section" 
          advancedEdit={advancedEdit} 
          resizable={true}
          className="w-full"
        >
          <div 
            className="rounded-2xl p-3 shadow-lg"
            style={{ backgroundColor: `${theme.accent}15` }}
          >
            <h3 
              className={`font-bold ${getConcertFontSize()} mb-2 text-left`}
              style={{ color: kuronekotheme.primary }}
            >
              🎤 {t('concert')}
            </h3>
            <div className="space-y-1">
              {editableData.concerts.slice(0, 3).map((concert, index) => (
                <div key={concert.id || index} className="bg-white p-2 rounded-xl shadow-sm text-left">
                  <div className={`${getConcertFontSize()} text-black`}>
                    <span className="font-bold">{formatConcertDate(concert.date)}</span> - <span className="font-medium">{concert.text}{concert.country_code ? `, ${concert.country_code}` : ''}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </DraggableElement>
      )}
    </DraggableSection>
  );

  const TracklistSection = ({ id = "tracklist", layoutClass = "col-span-3" }) => (
    <DraggableSection 
      id={id} 
      className={`${layoutClass} space-y-3`}
      advancedEdit={advancedEdit}
      onDragStart={handleDragStart}
      onDrop={handleDrop}
    >
      <div 
        className={`rounded-2xl p-3 shadow-lg ${advancedEdit ? 'border-2 border-dashed border-blue-300' : ''}`}
        style={{ backgroundColor: `${theme.primary}15` }}
      >
        <div className="text-center mb-2 flex justify-between items-center">
          <h3 className="text-base font-bold" style={{ color: theme.primary }}>
            TRACKLIST
          </h3>
          {advancedEdit && (
            <Button
              onClick={addTrack}
              size="sm"
              className="h-6 px-2 text-xs"
              style={{ backgroundColor: theme.primary }}
            >
              + Titre
            </Button>
          )}
        </div>
        
        <div className={`${getTrackContainerClasses(editableData.tracks.length)} flex-1`}>
          {editableData.tracks.slice(0, getMaxVisibleTracks()).map((track, index) => {
            const maxVisible = getMaxVisibleTracks();
            const isLastVisible = index === maxVisible - 1;
            const hasMoreTracks = editableData.tracks.length > maxVisible;
            
            return (
            <DraggableElement 
              key={index} 
              id={`track_${index}`} 
              advancedEdit={advancedEdit} 
              className="w-full"
            >
              <div className="bg-white p-1 rounded-lg shadow-sm group tracklist-item" style={{ borderBottom: index < maxVisible - 1 ? '1px solid #e5e7eb' : 'none' }}>
                <div className="flex items-center space-x-1">
                  <span 
                    className="font-bold flex-shrink-0 tracklist-number"
                    style={{ color: '#4DBA5F', fontSize: getTrackFontSize(editableData.tracks.length), minWidth: '24px' }}
                  >
                    {index + 1}.
                  </span>
                  {advancedEdit ? (
                    <Input
                      value={track}
                      onChange={(e) => editTrack(index, e.target.value)}
                      className={`${getTrackFontSize(editableData.tracks.length)} h-6 flex-1 border-none bg-transparent p-0 tracklist-title`}
                    />
                  ) : (
                    <span className={`${getTrackFontSize(editableData.tracks.length)} font-medium text-gray-700 flex-1 tracklist-title`} style={{ lineHeight: '1.2' }}>
                      {renderFormattedText(track)}
                      {/* Afficher les "..." sur le dernier titre visible s'il y a plus de titres */}
                      {isLastVisible && hasMoreTracks && (
                        <span style={{ color: '#4DBA5F', fontWeight: 'bold', marginLeft: '4px', fontSize: '18px' }}>...</span>
                      )}
                    </span>
                  )}
                  {advancedEdit && (
                    <Button
                      onClick={() => removeTrack(index)}
                      size="sm"
                      variant="ghost"
                      className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Trash2 className="h-3 w-3 text-red-500" />
                    </Button>
                  )}
                </div>
              </div>
            </DraggableElement>
            );
          })}
        </div>
      </div>

      {/* Media Mentions */}
      {editableData.selected_media.length > 0 && (
        <div 
          className={`rounded-2xl p-3 shadow-lg ${advancedEdit ? 'border-2 border-dashed border-green-300' : ''}`}
          style={{ backgroundColor: `${theme.secondary}15` }}
        >
          <h3 
            className={`font-bold ${getPromotionFontSize()} mb-2 text-left`}
            style={{ color: kuronekotheme.primary }}
          >
            📢 PROMOTION
          </h3>
          <div className="space-y-1">
            {editableData.selected_media.map((media, index) => (
              <div key={index} className="flex items-start gap-1 bg-white p-1 rounded-lg shadow-sm">
                <img 
                  src={`${process.env.REACT_APP_BACKEND_URL}/api/media-icons/${mediaFilenames[media] || `${media.toLowerCase().replace(/\s+/g, '_').replace(/&/g, 'and')}.png`}`}
                  alt={media}
                  className="w-6 h-6 object-contain mt-0.5 flex-shrink-0"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }}
                />
                <div 
                  className="w-1 h-1 rounded-full mt-1 flex-shrink-0"
                  style={{ backgroundColor: theme.secondary, display: 'none' }}
                ></div>
                <div className="flex-1">
                  <span className="text-xs font-bold text-black block">{media}</span>
                  {editableData.media_descriptions[media] && (
                    advancedEdit ? (
                      <Textarea
                        value={editableData.media_descriptions[media]}
                        onChange={(e) => {
                          const newDescriptions = { ...editableData.media_descriptions };
                          newDescriptions[media] = e.target.value;
                          setEditableData(prev => ({ ...prev, media_descriptions: newDescriptions }));
                        }}
                        className="text-xs h-8 mt-1 border-gray-200"
                      />
                    ) : (
                      <span className="text-xs text-black block" style={{ lineHeight: '0.95', margin: 0, padding: 0 }}>
                        {editableData.media_descriptions[media]}
                      </span>
                    )
                  )}
                </div>
                {advancedEdit && (
                  <Button
                    onClick={() => removeMedia(media)}
                    size="sm"
                    variant="ghost"
                    className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Trash2 className="h-3 w-3 text-red-500" />
                  </Button>
                )}
              </div>
            ))}
          </div>
          
          {/* Et Aussi - Media logos only, within media section */}
          {editableData.enable_et_aussi && editableData.et_aussi_medias && editableData.et_aussi_medias.length > 0 && (
            <div className="mt-3 pt-2 border-t border-gray-200">
              <h4 className="text-xs font-bold mb-2" style={{ color: kuronekotheme.primary }}>ET AUSSI :</h4>
              <div className="flex flex-wrap gap-2">
                {editableData.et_aussi_medias.map((media, index) => (
                  <div key={index} className="flex items-center justify-center w-10 h-10 bg-white rounded shadow-sm">
                    <img 
                      src={`${process.env.REACT_APP_BACKEND_URL}/api/media-icons/${mediaFilenames[media] || `${media.toLowerCase().replace(/\s+/g, '_').replace(/&/g, 'and')}.png`}`}
                      alt={media}
                      className="w-8 h-8 object-contain"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                    <div 
                      className="w-8 h-8 bg-gray-100 rounded flex items-center justify-center text-gray-600 font-bold text-xs"
                      style={{ display: 'none' }}
                    >
                      {media.substring(0, 2)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </DraggableSection>
  );

  // FONCTION D'EXPORT SCREENSHOT UNIQUEMENT
  const formatConcertDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('fr-FR', { 
        day: '2-digit', 
        month: '2-digit', 
        year: '2-digit' 
      });
    } catch {
      return dateStr;
    }
  };

  const getSocialLogo = (logoName) => {
    // Check if we have an uploaded image for this logo
    const logoData = logosList.find(l => l.name === logoName);
    if (logoData && logoData.filename) {
      return <img src={`${process.env.REACT_APP_BACKEND_URL}/api/social-icons/${logoData.filename}`} alt={logoName} className="w-6 h-6 object-contain" />;
    }
    // Fallback to hardcoded SVG logos
    const LogoComponent = SocialLogos[logoName];
    return LogoComponent ? <LogoComponent /> : <span className="text-xs font-bold">{logoName.charAt(0)}</span>;
  };

  // Determine album type based on track count - New Kuroneko specifications
  const getAlbumType = () => {
    // Retourne le type sélectionné dans le formulaire
    return editableData.album_type || "Single";
  };

  // Composant Argumentaire Redimensionnable
  const ResizableArgumentaire = ({ content, kuronekotheme }) => {
    const [size, setSize] = useState({ width: '100%', height: '100%' });
    const [isResizing, setIsResizing] = useState(false);
    const [resizeDirection, setResizeDirection] = useState(null);
    const containerRef = useRef(null);
    const startPos = useRef({ x: 0, y: 0, width: 0, height: 0 });

    const handleMouseDown = (direction) => (e) => {
      e.preventDefault();
      setIsResizing(true);
      setResizeDirection(direction);
      
      const rect = containerRef.current.getBoundingClientRect();
      startPos.current = {
        x: e.clientX,
        y: e.clientY,
        width: rect.width,
        height: rect.height
      };
    };

    const handleMouseMove = useCallback((e) => {
      if (!isResizing || !resizeDirection) return;

      const deltaX = e.clientX - startPos.current.x;
      const deltaY = e.clientY - startPos.current.y;

      let newWidth = startPos.current.width;
      let newHeight = startPos.current.height;

      // Calcul des nouvelles dimensions selon la direction
      if (resizeDirection.includes('right')) {
        newWidth = Math.max(200, startPos.current.width + deltaX);
      }
      if (resizeDirection.includes('left')) {
        newWidth = Math.max(200, startPos.current.width - deltaX);
      }
      if (resizeDirection.includes('bottom')) {
        newHeight = Math.max(100, startPos.current.height + deltaY);
      }
      if (resizeDirection.includes('top')) {
        newHeight = Math.max(100, startPos.current.height - deltaY);
      }

      setSize({ width: `${newWidth}px`, height: `${newHeight}px` });
    }, [isResizing, resizeDirection]);

    const handleMouseUp = useCallback(() => {
      setIsResizing(false);
      setResizeDirection(null);
    }, []);

    useEffect(() => {
      if (isResizing) {
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        return () => {
          document.removeEventListener('mousemove', handleMouseMove);
          document.removeEventListener('mouseup', handleMouseUp);
        };
      }
    }, [isResizing, handleMouseMove, handleMouseUp]);

    const handleStyle = {
      position: 'absolute',
      backgroundColor: '#4DBA5F',
      border: '2px solid #2d7a3f',
      cursor: 'grab',
      zIndex: 10
    };

    return (
      <div 
        ref={containerRef}
        style={{ 
          flex: '1 1 auto',
          padding: '5mm',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          marginTop: '0',
          position: 'relative',
          width: size.width,
          height: size.height,
          maxWidth: '100%',
          maxHeight: '100%'
        }}
      >
        {/* Bordure verte visible */}
        <div 
          className="p-3 rounded h-full"
          style={{ 
            backgroundColor: `${kuronekotheme.primary}15`,
            border: `2px dashed #4DBA5F`,
            overflow: 'auto',
            display: 'flex',
            alignItems: 'flex-start',
            position: 'relative'
          }}
        >
          <div className="text-[11px] font-medium text-left leading-relaxed text-black w-full" style={{ wordWrap: 'break-word', overflowWrap: 'break-word' }}>
            {content}
          </div>

          {/* Poignées de redimensionnement - Coins */}
          <div 
            onMouseDown={handleMouseDown('top-left')}
            style={{ 
              ...handleStyle,
              top: '-6px',
              left: '-6px',
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              cursor: 'nw-resize'
            }}
          />
          <div 
            onMouseDown={handleMouseDown('top-right')}
            style={{ 
              ...handleStyle,
              top: '-6px',
              right: '-6px',
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              cursor: 'ne-resize'
            }}
          />
          <div 
            onMouseDown={handleMouseDown('bottom-left')}
            style={{ 
              ...handleStyle,
              bottom: '-6px',
              left: '-6px',
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              cursor: 'sw-resize'
            }}
          />
          <div 
            onMouseDown={handleMouseDown('bottom-right')}
            style={{ 
              ...handleStyle,
              bottom: '-6px',
              right: '-6px',
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              cursor: 'se-resize'
            }}
          />

          {/* Poignées de redimensionnement - Bords */}
          <div 
            onMouseDown={handleMouseDown('top')}
            style={{ 
              ...handleStyle,
              top: '-6px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '40px',
              height: '12px',
              borderRadius: '6px',
              cursor: 'n-resize'
            }}
          />
          <div 
            onMouseDown={handleMouseDown('bottom')}
            style={{ 
              ...handleStyle,
              bottom: '-6px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '40px',
              height: '12px',
              borderRadius: '6px',
              cursor: 's-resize'
            }}
          />
          <div 
            onMouseDown={handleMouseDown('left')}
            style={{ 
              ...handleStyle,
              left: '-6px',
              top: '50%',
              transform: 'translateY(-50%)',
              width: '12px',
              height: '40px',
              borderRadius: '6px',
              cursor: 'w-resize'
            }}
          />
          <div 
            onMouseDown={handleMouseDown('right')}
            style={{ 
              ...handleStyle,
              right: '-6px',
              top: '50%',
              transform: 'translateY(-50%)',
              width: '12px',
              height: '40px',
              borderRadius: '6px',
              cursor: 'e-resize'
            }}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: theme.bg }}>
        {/* Control Bar */}
        <div className="sticky top-0 z-40 bg-white shadow-lg border-b no-print">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-4">
                <Button onClick={onBack} variant="outline" className="gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  Retour
                </Button>
                <h1 className="text-2xl font-bold text-gray-800">
                  Fiche Promotionnelle - {editableData.artist}
                </h1>
              </div>
              <div className="flex items-center gap-3">
                <Button
                  data-testid="download-pdf-sheet-btn"
                  onClick={() => { toast.dismiss(); window.print(); }}
                  variant="outline"
                  className="gap-2"
                >
                  <Download className="h-4 w-4" />
                  Télécharger en PDF
                </Button>
                {onSave && (
                  <Button
                    data-testid="save-sheet-btn"
                    onClick={onSave}
                    className="gap-2 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white"
                  >
                    <Save className="h-4 w-4" />
                    Enregistrer
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>

      {/* New Layout - Professional Music Sheet */}
      <div className="max-w-[1400px] mx-auto p-8 print-hide-container">
        <div 
          ref={artboardRef}
          className="promotional-sheet bg-white shadow-2xl rounded-3xl border-2 border-gray-100 print-hide-wrapper"
          style={{ 
            minHeight: '600px', 
            overflow: 'visible'
          }}
        >
          
          {/* Header supprimé - éléments déplacés dans le cadre A4 */}

          {/* Main Content Grid - A4 LANDSCAPE FORMAT STRICT - GRID LAYOUT */}
          <div 
            id="artboard"
            className="mx-auto bg-white shadow-2xl"
            style={{
              width: '297mm',
              height: '210mm',
              minHeight: '210mm',
              maxHeight: '210mm',
              padding: '3mm', // Réduit de 5mm à 3mm pour plus d'espace contenu
              boxSizing: 'border-box',
              display: 'grid',
              gridTemplateRows: 'auto 1fr',
              position: 'relative',
              gap: '0mm',
              overflow: 'hidden' // Force le contenu à ne pas déborder
            }}
          >
            {/* PREMIÈRE LIGNE : Logo Kuroneko (gauche) | 29Music (milieu) | Titre/sous-titre (droite) */}
            <div 
              className="grid grid-cols-12"
              style={{ gap: '2mm' }}
            >
              {/* Logo Kuroneko - aligné avec pochette */}
              <div 
                className="col-span-3"
                style={{ 
                  padding: '5px',
                  position: 'relative'
                }}
              >
                <div 
                  className="flex items-center justify-center px-2 py-1 rounded-lg shadow-lg"
                  style={{ 
                    backgroundColor: kuronekotheme.primary,
                    border: `3px solid ${kuronekotheme.primary}`
                  }}
                >
                  <div className="text-center">
                    <div className="text-white font-bold text-base tracking-wider mb-0.5" style={{ fontFamily: 'Raleway, sans-serif' }}>
                      Kuroneko
                    </div>
                    <div className="text-white font-medium whitespace-nowrap" style={{ fontSize: getFontSizeInPx('kuroneko_logo', '9px'), fontFamily: getFontFamily('kuroneko_logo', 'Arial, sans-serif') }}>
                      commercial.distribution@kuroneko-media.com
                    </div>
                    <div className="text-white text-[10px] font-medium">
                      +33 1 49 72 00 68
                    </div>
                    <div className="flex justify-center gap-2 mt-0.5">
                      <a href={editableData.kuroneko_website || "https://kuroneko.fr"} target="_blank" rel="noopener noreferrer">
                        <svg className="w-4 h-4 text-white hover:text-gray-200" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4.083 9h1.946c.089-1.546.383-2.97.837-4.118C6.004 6.07 4.8 7.404 4.083 9zM10 2C5.582 2 2 5.582 2 10s3.582 8 8 8 8-3.582 8-8-3.582-8-8-8zm0 2c-.424 0-.852.142-1.25.424.46 1.032.748 2.31.837 3.826h.826c.089-1.516.377-2.794.837-3.826C10.852 4.142 10.424 4 10 4zm4.917 5c-.717-1.596-1.921-2.93-2.783-4.118.454 1.148.748 2.572.837 4.118h1.946zM15.917 11c-.089 1.546-.383 2.97-.837 4.118.862-1.188 2.066-2.522 2.783-4.118h-1.946zm-5.104 0H9.187c-.089 1.516-.377 2.794-.837 3.826.398.282.826.424 1.25.424s.852-.142 1.25-.424c-.46-1.032-.748-2.31-.837-3.826z" clipRule="evenodd" />
                        </svg>
                      </a>
                      <a href={editableData.kuroneko_facebook || "https://facebook.com/kuroneko"} target="_blank" rel="noopener noreferrer">
                        <svg className="w-4 h-4 text-white hover:text-gray-200" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M20 10c0-5.523-4.477-10-10-10S0 4.477 0 10c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V10h2.54V7.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V10h2.773l-.443 2.89h-2.33v6.988C16.343 19.128 20 14.991 20 10z" clipRule="evenodd" />
                        </svg>
                      </a>
                      <a href={editableData.kuroneko_instagram || "https://instagram.com/kuroneko"} target="_blank" rel="noopener noreferrer">
                        <svg className="w-4 h-4 text-white hover:text-gray-200" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 2c2.134 0 2.388.01 3.233.047.778.036 1.201.166 1.484.275.372.145.64.318.92.598.28.28.453.548.598.92.109.283.239.706.275 1.484.037.845.047 1.099.047 3.233s-.01 2.388-.047 3.233c-.036.778-.166 1.201-.275 1.484-.145.372-.318.64-.598.92-.28.28-.548.453-.92.598-.283.109-.706.239-1.484.275-.845.037-1.099.047-3.233.047s-2.388-.01-3.233-.047c-.778-.036-1.201-.166-1.484-.275-.372-.145-.64-.318-.92-.598-.28-.28-.453-.548-.598-.92-.109-.283-.239-.706-.275-1.484-.037-.845-.047-1.099-.047-3.233s.01-2.388.047-3.233c.036-.778.166-1.201.275-1.484.145-.372.318-.64.598-.92.28-.28.548-.453.92-.598.283-.109.706-.239 1.484-.275C7.612 2.01 7.866 2 10 2zm0 1.622c-2.1 0-2.345.009-3.17.045-.765.035-1.18.158-1.456.263-.366.142-.628.312-.903.587-.275.275-.445.537-.587.903-.105.276-.228.691-.263 1.456-.036.825-.045 1.07-.045 3.17s.009 2.345.045 3.17c.035.765.158 1.18.263 1.456.142.366.312.628.587.903.275.275.537.445.903.587.276.105.691.228 1.456.263.825.036 1.07.045 3.17.045s2.345-.009 3.17-.045c.765-.035 1.18-.158 1.456-.263.366-.142.628-.312.903-.587.275-.275.445-.537.587-.903.105-.276.228-.691.263-1.456.036-.825.045-1.07.045-3.17s-.009-2.345-.045-3.17c-.035-.765-.158-1.18-.263-1.456-.142-.366-.312-.628-.587-.903-.275-.275-.537-.445-.903-.587-.276-.105-.691-.228-1.456-.263-.825-.036-1.07-.045-3.17-.045zm0 2.784a3.593 3.593 0 110 7.186 3.593 3.593 0 010-7.186zm0 5.915a2.322 2.322 0 100-4.644 2.322 2.322 0 000 4.644zm4.556-6.086a.84.84 0 11-1.68 0 .84.84 0 011.68 0z" clipRule="evenodd" />
                        </svg>
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Coup de cœur - Espace réservé même si vide */}
              <div 
                className="col-span-2 flex items-center justify-center"
                style={{ 
                  padding: '5mm',
                  paddingLeft: '7mm',
                  position: 'relative',
                  minHeight: '60px'
                }}
              >
                {editableData.coup_de_coeur && (
                  <img 
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/${isExport() ? 'staff_picks' : 'coup_de_coeur'}.png`}
                    alt={isExport() ? "Staff picks" : "Coup de cœur"}
                    style={{ 
                      display: 'block',
                      maxWidth: '100%',
                      maxHeight: '55px',
                      width: 'auto',
                      height: 'auto',
                      objectFit: 'contain'
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                )}
              </div>

              {/* Titre/Sous-titre */}
              <div 
                className="col-span-3 flex items-center justify-center"
                style={{ 
                  padding: '5px',
                  position: 'relative'
                }}
              >
                <div className="text-center">
                  <h1 
                    className="font-bold text-gray-900 leading-tight"
                    style={{ 
                      fontSize: getFontSizeInPx('artist_title', '20px'),
                      fontFamily: getFontFamily('artist_title', 'Arial, sans-serif')
                    }}
                  >
                    {editableData.artist.toUpperCase()}
                  </h1>
                  <h2 
                    className="text-gray-700 leading-tight italic"
                    style={{ 
                      fontSize: getFontSizeInPx('title', '15px'),
                      fontFamily: getFontFamily('artist_title', 'Arial, sans-serif')
                    }}
                  >  
                    {editableData.album}
                  </h2>
                  <div className="mt-1">
                    <span className="text-[9px] font-medium px-1 py-0.5 rounded-full" style={{ 
                      backgroundColor: `${kuronekotheme.primary}20`, 
                      color: kuronekotheme.primary 
                    }}>- {getAlbumType()} -</span>
                  </div>
                </div>
              </div>

              {/* 29Music Logo */}
              <div 
                className="col-span-2 flex items-center justify-center"
                style={{ 
                  padding: '5mm',
                  position: 'relative'
                }}
              >
                {(() => {
                  const custom = editableData.size_label_logo && editableData.size_label_logo !== 'default'
                    ? parseInt(editableData.size_label_logo) : null;
                  const boxStyle = custom
                    ? { width: `${custom}px`, height: `${Math.round(custom * 12 / 35)}px` }
                    : { width: '35mm', height: '12mm' };
                  return (
                <div style={{ ...boxStyle, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <img 
                  src={editableData.selected_label && labelLogos.find(l => l.name === editableData.selected_label)?.filename 
                    ? `${process.env.REACT_APP_BACKEND_URL}/api/label-logos/${labelLogos.find(l => l.name === editableData.selected_label).filename}`
                    : `${process.env.REACT_APP_BACKEND_URL}/api/label-logos/29Music.png`}
                  alt={editableData.selected_label || "Label Logo"}
                  style={{ 
                    display: 'block',
                    maxWidth: '100%',
                    maxHeight: '100%',
                    width: 'auto',
                    height: 'auto',
                    objectFit: 'contain'
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    if (e.target.nextSibling) {
                      e.target.nextSibling.style.display = 'flex';
                    }
                  }}
                />
                <div 
                  className="flex items-center justify-center bg-gray-100 text-gray-600 font-bold text-lg px-4 py-2 rounded"
                  style={{ 
                    display: 'none',
                    maxWidth: '100%',
                    maxHeight: '100%'
                  }}
                >
                  29MUSIC
                </div>
                </div>
                  );
                })()}
              </div>

              {/* Genre */}
              <div 
                className="col-span-2 flex items-center justify-center"
                style={{ 
                  padding: '5mm',
                  position: 'relative'
                }}
              >
                <div 
                  className="genre-badge rounded-lg shadow-lg"
                  style={{ 
                    backgroundColor: theme.primary,
                    border: `3px solid ${theme.primary}`,
                    padding: '8px 16px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    height: '100%'
                  }}
                >
                  <h3 className="font-bold text-white text-center leading-tight" style={{ fontSize: getFontSizeInPx('genre', '18px'), fontFamily: getFontFamily('genre', 'Arial, sans-serif') }}>
                    {getGenreLabel().toUpperCase()}
                  </h3>
                  {editableData.sous_genre && (
                    <div className="text-white font-normal text-center mt-1" style={{ fontSize: getFontSizeInPx('genre', '12px'), fontFamily: getFontFamily('genre', 'Arial, sans-serif') }}>
                      {editableData.sous_genre}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* STRUCTURE FINALE : COL1 | (ARGUMENTAIRE au-dessus de COL2+COL3) */}
            <div 
              style={{ 
                display: 'grid',
                gridTemplateColumns: '25% 1fr',
                gridTemplateRows: 'auto 1fr',
                gap: '2mm',
                flex: 1,
                minHeight: 0,
                maxHeight: '100%',
                overflow: 'hidden'
              }}
            >
              {/* COLONNE 1 : Structure complète avec 2 encadrements */}
              <div 
                style={{ 
                  gridRow: '1 / 3',
                  position: 'relative',
                  padding: '2mm',
                  overflow: 'hidden'
                }}
              >
                {/* ENCADREMENT 1 : Pochette + Boutons - Position absolue depuis le haut - ALIGNÉ avec argumentaire */}
                <div style={{ 
                  position: 'absolute',
                  top: '0mm',
                  left: '0mm',
                  right: '2mm',
                  height: getCoverBlockHeight(),
                }}>
                  <div style={{ 
                    border: '2px solid #4DBA5F', 
                    borderRadius: '4px', 
                    padding: '2mm',
                    backgroundColor: '#fff',
                    height: '100%',
                    overflow: 'hidden'
                  }}>
                    <div style={{ transform: 'scale(0.45)', transformOrigin: 'top left', width: '222%', marginBottom: '-115px' }}>
                      <LandscapeCoverAndButtons />
                    </div>
                  </div>
                </div>

                {/* ENCADREMENT 2 : PC EXPRESS + 3 SUPPORTS CD/LP - Position absolue en bas */}
                <div style={{ 
                  position: 'absolute',
                  bottom: '0mm',  // Au bord du bas (était 1mm)
                  left: '0mm',  // Agrandi de 2mm à gauche (était 2mm)
                  right: '2mm',
                  height: '85mm',  // Augmenté de 1mm pour garder la ligne du haut fixe (était 84mm)
                  overflow: 'visible', 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: '2mm',
                  border: '2px solid #4DBA5F',
                  borderRadius: '4px',
                  padding: '2mm',
                  backgroundColor: '#fff'
                }}>
                  {/* PC EXPRESS en haut */}
                  {(editableData.pc_type || editableData.pc_expres || editableData.date_de_sortie || editableData.fin_de_preco) && (
                    <div style={{ flex: '0 0 auto' }}>
                      <PCExpressBlock />
                    </div>
                  )}
                  
                  {/* 3 Supports CD/LP en dessous */}
                  <div style={{ flex: '1 1 auto', overflow: 'visible' }}>
                    <LandscapeLeftColumn />
                  </div>
                </div>
              </div>

              {/* ARGUMENTAIRE */}
              <div 
                style={{ 
                  gridColumn: '2',
                  gridRow: '1',
                  border: '2px solid #4DBA5F',
                  padding: '3mm',
                  borderRadius: '4px',
                  overflow: 'visible',
                  minHeight: 'auto'
                }}
              >
                <div style={{ 
                  fontFamily: getFontFamily('argumentaire', 'Arial, sans-serif'), 
                  fontSize: getFontSizeInPx('argumentaire', '11px'), 
                  fontWeight: '500', 
                  textAlign: 'left', 
                  lineHeight: '1.3', 
                  color: 'black', 
                  wordWrap: 'break-word' 
                }}>
                  {renderFormattedText(formatArgumentaire(editableData.argumentaire))}
                </div>
              </div>

              {/* COLONNES 2 ET 3 : Tracklist | Promo/Concerts - qui s'étend jusqu'en bas */}
              <div 
                style={{ 
                  gridColumn: '2',
                  gridRow: '2',
                  display: 'grid',
                  gridTemplateColumns: (() => {
                    // Calcul du ratio dynamique basé sur tracklist_width
                    const tracklistPercent = editableData.tracklist_width && editableData.tracklist_width !== 'default' 
                      ? parseInt(editableData.tracklist_width) 
                      : 30; // Valeur par défaut = 30%
                    const promotionPercent = 100 - tracklistPercent;
                    // Convertir en fr (fraction units)
                    const tracklistFr = tracklistPercent / 100;
                    const promotionFr = promotionPercent / 100;
                    return `${tracklistFr}fr ${promotionFr}fr`;
                  })(),
                  gap: '2mm',
                  overflow: 'hidden',
                  alignSelf: 'stretch',
                  height: '100%'
                }}
              >
                {/* Tracklist - ENCADREMENT VERT */}
                <div 
                  style={{ 
                    border: '2px solid #4DBA5F',
                    borderRadius: '4px',
                    padding: '2mm',
                    overflow: 'hidden',
                    backgroundColor: '#fff',
                    height: '100%'
                  }}
                >
                  <LandscapeCenterColumn />
                </div>
                
                {/* Promo/Concerts - ENCADREMENT VERT */}
                <div 
                  style={{ 
                    border: '2px solid #4DBA5F',
                    borderRadius: '4px',
                    padding: '2mm',
                    overflow: 'hidden',
                    backgroundColor: '#fff',
                    height: '100%'
                  }}
                >
                  <LandscapeRightColumn />
                </div>
              </div>
            </div>
          </div>
          
        </div>
      </div>

      {/* Print Styles for A4 Landscape */}
      <style>{`
        @media print {
          /* Page setup */
          @page {
            size: A4 landscape;
            margin: 0;
          }
          
          /* Hide non-printable elements */
          .no-print { 
            display: none !important; 
          }
          
          /* Les notifications (sonner) ne doivent jamais apparaître sur le PDF */
          [data-sonner-toaster],
          [data-sonner-toast],
          .toaster {
            display: none !important;
          }
          
          /* Basic reset */
          body { 
            margin: 0; 
            padding: 0; 
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          
          .sticky { 
            position: static !important; 
          }
          
          /* Container wrappers - minimize their impact */
          .print-hide-container {
            max-width: none !important;
            padding: 0 !important;
            margin: 0 !important;
          }
          
          .print-hide-wrapper {
            box-shadow: none !important;
            border: none !important;
            border-radius: 0 !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          
          /* Promotional sheet - A4 landscape dimensions */
          .promotional-sheet {
            width: 297mm !important;
            height: 210mm !important;
            max-width: 297mm !important;
            max-height: 210mm !important;
            min-height: 210mm !important;
            overflow: hidden !important;
            box-shadow: none !important;
            border-radius: 0 !important;
          }
          
          /* Artboard - the actual A4 content */
          #artboard {
            width: 297mm !important;
            height: 210mm !important;
            max-width: 297mm !important;
            max-height: 210mm !important;
            overflow: hidden !important;
            padding: 3mm !important;
            box-sizing: border-box !important;
            box-shadow: none !important;
            border-radius: 0 !important;
          }
          
          /* Prevent page breaks */
          #artboard, #artboard * {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
          
          /* Remove decorative shadows */
          .shadow-lg, .shadow-xl, .shadow-2xl, .shadow-md {
            box-shadow: none !important;
          }
          
          /* Minimal border radius */
          .rounded-3xl, .rounded-2xl {
            border-radius: 4px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default WebPPTGenerator;